/**
 * Herstelcode-beheer in de instellingen van de leerling.
 *
 * ─── WAAROM DIT BESTAAT ─────────────────────────────────────────────────────
 *
 * De herstelcode werd precies één keer getoond: op het succesScherm na
 * registratie. Voor een kind van acht is dat het scherm dat tussen hem en de
 * app in staat — dat wordt weggeklikt, niet overgeschreven. En daarna was er
 * geen enkele weg terug: geen scherm, geen knop, geen melding dat er iets
 * ontbrak.
 *
 * Het gevolg merkt niemand op het moment dat het gebeurt. Het komt maanden
 * later boven, precies wanneer het kind zijn wachtwoord kwijt is en het
 * vangnet nodig heeft dat het nooit heeft opgepakt.
 *
 * ─── WAAROM "NIEUWE CODE" EN GEEN "TOON MIJN CODE" ──────────────────────────
 *
 * De code staat bcrypt-gehasht op de server. Wij kunnen hem niet teruglezen —
 * en dat is geen beperking maar het punt: voor een kind zonder e-mailadres is
 * die code tegelijk het identiteitsbewijs waarmee het een inzage- of
 * verwijderverzoek kan doen. Konden wij hem lezen, dan konden wij ons als dat
 * kind voordoen.
 *
 * Er is dus maar één eerlijke uitweg: een nieuwe maken, waarmee de oude
 * vervalt. Dat staat er ook zo bij, want een kind dat de oude wél had
 * opgeschreven moet weten dat dat papiertje nu waardeloos is.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Copy, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { createAndStoreRecoveryCode, fetchRecoveryCodeStatus } from '../../data/supabase/recoveryCode';
import { error as logError } from '../../utils/logger';
type Status = {
  kind: 'loading';
} | {
  kind: 'unknown';
} | {
  kind: 'missing';
} | {
  kind: 'present';
  setAt: string | null;
};
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}
export function RecoveryCodeSection() {
  const [status, setStatus] = useState<Status>({
    kind: 'loading'
  });
  const [freshCode, setFreshCode] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const load = useCallback(async () => {
    const result = await fetchRecoveryCodeStatus();
    if (result === null) {
      // Bewust "onbekend" en niet "ontbreekt": bij een storing zou een
      // waarschuwing het kind laten denken dat het onbeschermd is.
      setStatus({
        kind: 'unknown'
      });
      return;
    }
    setStatus(result.hasCode ? {
      kind: 'present',
      setAt: result.setAt
    } : {
      kind: 'missing'
    });
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const handleGenerate = async () => {
    setIsWorking(true);
    setFailed(false);
    setCopied(false);
    try {
      const code = await createAndStoreRecoveryCode();
      if (!code) {
        // Geen code tonen die de server niet kent. Een opgeschreven code die
        // nergens bij hoort is erger dan geen code.
        setFailed(true);
        return;
      }
      setFreshCode(code);
      setConfirming(false);
      await load();
    } catch (err) {
      logError('[RecoveryCodeSection] nieuwe code maken mislukt', err);
      setFailed(true);
    } finally {
      setIsWorking(false);
    }
  };
  const handleCopy = async () => {
    if (!freshCode) return;
    try {
      await navigator.clipboard.writeText(freshCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logError('[RecoveryCodeSection] kopiëren mislukt', err);
    }
  };
  const setAtLabel = status.kind === 'present' ? formatDate(status.setAt) : null;
  return <section className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2082" data-mp="jumdi94z3tj3">
      <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2083" data-mp="z3rjrgyxmu5m">
        <KeyRound className="w-5 h-5 text-sky-600" aria-hidden="true" data-id="element-2084" data-mp="uc9eigpvm7eu" />
        Je geheime herstelcode
      </h3>
      <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2085" data-mp="peq27mcqo4pj">
        Met deze code kom je weer binnen als je je wachtwoord vergeet. Hou hem
        geheim — deel hem met niemand, ook niet met een vriend.
      </p>

      {status.kind === 'loading' && <p className="text-sm text-slate-500" role="status" data-id="element-2086" data-mp="1i09e8656185k">
          Even kijken…
        </p>}

      {status.kind === 'unknown' && <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-id="element-2087" data-mp="191fdq4oprxmq">
          <p className="text-sm text-slate-600 font-medium" data-id="element-2088" data-mp="193ibxc00sx2j">
            We konden nu niet controleren of je een herstelcode hebt. Probeer
            het straks nog eens.
          </p>
          <Button variant="ghost" onClick={load} className="mt-2 text-sm text-slate-600" data-id="element-2089" data-mp="3tptmgb8i1zl5">
          
            Opnieuw proberen
          </Button>
        </div>}

      {/* Dit is het geval dat deze sectie bestaansrecht geeft: een kind dat
           zijn code destijds heeft weggeklikt, of een account van vóór deze
           functie. Zonder melding is dat niet te weten. */}
      {status.kind === 'missing' && !freshCode && <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-id="element-2090" data-mp="2i25tw8o0ehbn">
          <p className="flex items-start gap-2 text-sm text-amber-900 font-bold" data-id="element-2091" data-mp="2i4a8bkjjayq6">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-2092" data-mp="2n0quwab35515" />
          
            Je hebt nog geen herstelcode
          </p>
          <p className="mt-1 text-sm text-amber-800 font-medium" data-id="element-2093" data-mp="2wywwsrww6b60">
            Vergeet je nu je wachtwoord, dan kun je er niet zelf weer in. Maak
            er één en schrijf hem thuis op.
          </p>
          <Button onClick={handleGenerate} disabled={isWorking} className="mt-3" data-id="element-2094" data-mp="12lx9ecm9sco6">
          
            {isWorking ? 'Bezig…' : 'Maak mijn herstelcode'}
          </Button>
        </div>}

      {status.kind === 'present' && !freshCode && <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-id="element-2095" data-mp="29ypp3p5b981x">
          <p className="text-sm text-emerald-900 font-bold" data-id="element-2096" data-mp="24zmpwlc0hw1v">
            Je hebt een herstelcode
          </p>
          <p className="mt-1 text-sm text-emerald-800 font-medium" data-id="element-2097" data-mp="2ize1ygiyx2d8">
            {setAtLabel ? `Gemaakt op ${setAtLabel}.` : 'Hij staat veilig opgeslagen.'}{' '}
            We kunnen hem niet nog een keer laten zien — zo blijft hij echt
            alleen van jou.
          </p>

          {!confirming ? <Button variant="ghost" onClick={() => setConfirming(true)} className="mt-2 text-sm text-emerald-800" data-id="element-2098" data-mp="2nwo2ar7wayfu">
          
              <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" data-id="element-2099" data-mp="32lshdzje8mjq" />
              Ik ben hem kwijt — maak een nieuwe
            </Button> : <div className="mt-3 rounded-lg bg-white border border-emerald-200 p-3" data-id="element-2100" data-mp="3bn9xvj0pqq8y">
              <p className="text-sm text-slate-700 font-medium" data-id="element-2101" data-mp="3bkxf53n4sbuu">
                Je krijgt dan een nieuwe code.{' '}
                <strong className="font-bold" data-id="element-2102" data-mp="3qa8lf2wg90ie">
                  Je oude code werkt daarna niet meer
                </strong>
                , ook niet als je hem nog op papier hebt.
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-2103" data-mp="3h9kgqysy4ci6">
                <Button onClick={handleGenerate} disabled={isWorking} data-id="element-2104" data-mp="3h910rnmuvy6s">
                  {isWorking ? 'Bezig…' : 'Ja, maak een nieuwe'}
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)} data-id="element-2105" data-mp="37j7locni90xs">
                  Laat maar
                </Button>
              </div>
            </div>}
        </div>}

      {freshCode && <div className="rounded-xl bg-sky-50 border-2 border-sky-200 p-4" data-id="element-2106" data-mp="2xm2z4bpqz3gg">
          <p className="text-sm text-sky-900 font-bold mb-1" data-id="element-2107" data-mp="32kecgwf93w3f">
            Schrijf hem over en bewaar hem thuis
          </p>
          <p className="text-sm text-sky-800 font-medium mb-3" data-id="element-2108" data-mp="2okdafdn92imd">
            Je ziet hem maar één keer. Sluit je dit scherm, dan is hij weg.
          </p>
          {/* `select-all` + break-words: zes woorden met streepjes moeten op een
             telefoon in één keer te pakken zijn, niet half. */}
          <p className="select-all break-words rounded-lg bg-white border border-sky-200 px-3 py-2 font-mono text-lg font-bold tracking-tight text-slate-900" data-id="element-2109" data-mp="342d7xc0hdcnr">
            {freshCode}
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2110" data-mp="2v2q2mf4mrmpf">
            <Button variant="ghost" onClick={handleCopy} data-id="element-2111" data-mp="2v4az4aai9fpx">
              {copied ? <>
                  <Check className="w-4 h-4 mr-1.5" aria-hidden="true" data-id="element-2112" data-mp="2q4yd1rrgkcoe" />
                  Gekopieerd
                </> : <>
                  <Copy className="w-4 h-4 mr-1.5" aria-hidden="true" data-id="element-2113" data-mp="2gd24tnvnr66g" />
                  Kopieer
                </>}
            </Button>
            <Button onClick={() => setFreshCode(null)} data-id="element-2114" data-mp="2bf0imu2xqqqr">
              Ik heb hem opgeschreven
            </Button>
          </div>
        </div>}

      {failed && <p role="alert" className="mt-3 text-sm text-red-700 font-medium" data-id="element-2115" data-mp="22dat5tsm61vm">
          Het maken van een herstelcode is niet gelukt. Je oude code blijft
          gewoon werken. Probeer het straks nog eens.
        </p>}
    </section>;
}